import * as XLSX from 'xlsx';
import { normalizeAsRepairToken } from './asRepairCalculations';
import {
    applyAsRepairAutoValuesToForm,
    AS_REPAIR_MANAGEMENT_SCOPE,
    buildEmptyAsRepairFormValues,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
} from './asRepairFormConfig';

/** A/S 수리 관리 — 엑셀 열 고정 (1행부터 데이터, 헤더 없음) */
export const AS_REPAIR_EXCEL_COLUMN_MAPPING = [
    { column: 'A', index: 0, role: 'boxNumber', label: '박스 번호' },
    { column: 'B', index: 1, role: 'modelName', label: '모델명' },
    { column: 'C', index: 2, role: 'siteName', label: '현장명' },
    { column: 'D', index: 3, role: 'totalQty', label: '전체 수량' },
    { column: 'E', index: 4, role: 'shipDate', label: '출고일자' },
];

/** A/S 개인 관리 — 엑셀 열 고정 (1행 헤더, 2행부터 데이터) */
export const AS_REPAIR_PERSONAL_EXCEL_COLUMN_MAPPING = [
    { column: 'A', index: 0, role: 'boxNumber', label: '박스번호' },
    { column: 'B', index: 1, role: 'modelName', label: '모델명' },
    { column: 'C', index: 2, role: 'itemName', label: '품목명' },
    { column: 'F', index: 5, role: 'optionItem', label: '옵션사항' },
    { column: 'G', index: 6, role: 'siteName', label: '현장명' },
    { column: 'H', index: 7, role: 'defectName', label: '불량명' },
    { column: 'I', index: 8, role: 'totalQty', label: '전체수량' },
    { column: 'J', index: 9, role: 'shipDate', label: '출고일' },
];

const EXCEL_ROLE_FORM_LABEL_HINTS = {
    boxNumber: ['박스 번호', '박스번호', '박스'],
    modelName: ['모델명', '모델', '품목코드'],
    itemName: ['품목명', '품목'],
    optionItem: ['옵션사항', '옵션'],
    siteName: ['현장명', '현장'],
    defectName: ['불량명', '불량'],
    totalQty: ['전체 수량', '전체수량'],
    shipDate: ['출고일자', '출고일'],
    memo: ['memo', '비고', '비고사항', '메모'],
};

const normFieldLabel = (label) => String(label || '').replace(/\s+/g, '');
const normFieldKey = (key) => String(key || '').replace(/\s+/g, '');

export const getAsRepairExcelColumnMapping = (managementScope) => (
    managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL
        ? AS_REPAIR_PERSONAL_EXCEL_COLUMN_MAPPING
        : AS_REPAIR_EXCEL_COLUMN_MAPPING
);

const isOtherQtyFormField = (field) => {
    const nl = normalizeAsRepairToken(field.label);
    return (
        nl.includes('실입고')
        || nl.includes('진행중')
        || nl.includes('수리완료')
        || nl.includes('완료수량')
        || nl.includes('부족')
        || (nl.includes('입고') && !nl.includes('전체'))
    );
};

const labelMatchesHint = (field, hint) => {
    const label = normFieldLabel(field?.label);
    const h = normFieldLabel(hint);
    if (!label || !h) {
        return false;
    }
    return label === h || label.includes(h);
};

const pickFormFieldByRole = (fields, role, excludeKeys = new Set()) => {
    const pool = fields.filter((f) => f?.key && !excludeKeys.has(f.key));

    if (role === 'boxNumber') {
        const byKey = pool.find((f) => normFieldKey(f.key) === '박스번호');
        if (byKey) {
            return byKey;
        }
    }
    if (role === 'memo') {
        const byKey = pool.find((f) => f.key === 'memo');
        if (byKey) {
            return byKey;
        }
    }

    // 라벨 우선 매칭 (항목키가 잘못돼 있어도 올바른 항목으로 매핑)
    const hints = EXCEL_ROLE_FORM_LABEL_HINTS[role] || [];
    for (const hint of hints) {
        const exact = pool.find((f) => normFieldLabel(f.label) === normFieldLabel(hint));
        if (exact) {
            return exact;
        }
    }
    for (const hint of hints) {
        const found = pool.find((f) => labelMatchesHint(f, hint));
        if (found) {
            // 현장명 역할에 "전체수량" 등 다른 라벨 필드가 잡히지 않게 가드
            if (role === 'siteName') {
                const nl = normalizeAsRepairToken(found.label);
                if (nl.includes('전체') || nl.includes('수량') || nl.includes('옵션') || nl.includes('불량')) {
                    continue;
                }
            }
            if (role === 'totalQty') {
                const nl = normalizeAsRepairToken(found.label);
                if (nl.includes('현장') || !nl.includes('전체')) {
                    continue;
                }
            }
            return found;
        }
    }

    if (role === 'modelName') {
        return pool.find((f) => f.key === 'productName') || null;
    }
    if (role === 'siteName') {
        // key=siteName 이어도 라벨이 현장명이 아니면 사용하지 않음
        // (현재 설정에서 전체수량 항목키가 siteName 으로 잘못된 경우 대응)
        const byKey = pool.find((f) => {
            if (f.key !== 'siteName') {
                return false;
            }
            const nl = normalizeAsRepairToken(f.label);
            return nl.includes('현장') && !nl.includes('전체');
        });
        return byKey || null;
    }
    if (role === 'totalQty') {
        const qtyFields = pool.filter(
            (f) => (f.inputType === 'quantity' || f.inputType === 'auto') && !isOtherQtyFormField(f),
        );
        const byLabel = qtyFields.find((f) => {
            const nl = normalizeAsRepairToken(f.label);
            return nl.includes('전체수량') || nl === '전체' || (nl.includes('전체') && nl.includes('수량'));
        });
        if (byLabel) {
            return byLabel;
        }
        // 라벨에 전체수량이면 타입이 아니어도 매칭 (키가 siteName 인 경우 포함)
        return pool.find((f) => {
            const nl = normalizeAsRepairToken(f.label);
            return nl.includes('전체수량') || (nl.includes('전체') && nl.includes('수량'));
        }) || null;
    }
    if (role === 'shipDate') {
        return pool.find((f) => normalizeAsRepairToken(f.label).includes('출고')) || null;
    }
    return null;
};

/** 엑셀 열 → A/S 설정 필드 (역할별 필드 중복 사용 방지, 라벨 우선) */
export const getAsRepairExcelFieldMap = (config, managementScope = AS_REPAIR_MANAGEMENT_SCOPE.REPAIR) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const columnMapping = getAsRepairExcelColumnMapping(managementScope);
    const map = {};
    const usedKeys = new Set();
    // totalQty / siteName 은 키 오설정(siteName→전체수량) 때문에 라벨 우선으로 먼저 확정
    const priorityRoles = ['totalQty', 'siteName', 'boxNumber', 'modelName', 'itemName', 'optionItem', 'defectName', 'shipDate', 'memo'];
    const roles = [...new Set([
        ...priorityRoles.filter((role) => columnMapping.some((col) => col.role === role)),
        ...columnMapping.map((col) => col.role),
    ])];
    roles.forEach((role) => {
        const field = pickFormFieldByRole(fields, role, usedKeys);
        map[role] = field;
        if (field?.key) {
            usedKeys.add(field.key);
        }
    });
    return map;
};

const cellTextAsIs = (value) => {
    if (value == null || value === '') {
        return '';
    }
    return String(value);
};

const getCellRaw = (sheet, rowIndex, colIndex) => {
    const addr = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
    const cell = sheet[addr];
    if (!cell) {
        return '';
    }
    if (cell.w != null && String(cell.w).trim() !== '') {
        return cell.w;
    }
    if (cell.v == null) {
        return '';
    }
    return cellTextAsIs(cell.v);
};

const setExcelFieldValue = (values, field, raw, role = null) => {
    if (!field) {
        return;
    }
    if (role === 'shipDate') {
        values[field.key] = cellTextAsIs(raw);
        return;
    }
    if (field.inputType === 'quantity') {
        if (raw === '' || raw == null) {
            values[field.key] = '';
            return;
        }
        values[field.key] = parseQuantityValue(raw);
        return;
    }
    values[field.key] = cellTextAsIs(raw);
};

const rowHasAnyMappedData = (sheet, rowIndex, columnMapping) => (
    columnMapping.some(({ index }) => (
        String(getCellRaw(sheet, rowIndex, index)).trim() !== ''
    ))
);

/** DB 매칭용 — 폼 항목 키와 무관하게 현장명·모델명을 표준 키에 복사 */
const syncExcelRowMatchKeys = (values, fieldMap, sheet, rowIndex, columnMapping) => {
    const modelField = fieldMap.modelName;
    if (modelField?.key) {
        const modelRaw = values[modelField.key];
        if (modelRaw != null && String(modelRaw).trim() !== '') {
            values.productName = cellTextAsIs(modelRaw);
        }
    }
    if (!values.productName) {
        const modelCol = columnMapping.find((col) => col.role === 'modelName');
        if (modelCol) {
            const raw = getCellRaw(sheet, rowIndex, modelCol.index);
            if (String(raw).trim() !== '') {
                values.productName = cellTextAsIs(raw);
            }
        }
    }

    const siteField = fieldMap.siteName;
    if (siteField?.key) {
        const siteRaw = values[siteField.key];
        if (siteRaw != null && String(siteRaw).trim() !== '') {
            values.siteName = cellTextAsIs(siteRaw);
        }
    }
    if (!values.siteName) {
        const siteCol = columnMapping.find((col) => col.role === 'siteName');
        if (siteCol) {
            const raw = getCellRaw(sheet, rowIndex, siteCol.index);
            if (String(raw).trim() !== '') {
                values.siteName = cellTextAsIs(raw);
            }
        }
    }
};

/**
 * XLSX / XLSB → A/S form values 배열
 * @returns {{ rows: object[], skipped: number, errors: string[] }}
 */
export const parseAsRepairExcelFile = async (file, config, { managementScope } = {}) => {
    const errors = [];
    if (!file) {
        return { rows: [], skipped: 0, errors: ['파일이 없습니다.'] };
    }

    const ext = String(file.name || '').split('.').pop()?.toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xlsb') {
        return { rows: [], skipped: 0, errors: ['XLSX, XLSB 파일만 업로드할 수 있습니다.'] };
    }

    const scope = managementScope || AS_REPAIR_MANAGEMENT_SCOPE.REPAIR;
    const columnMapping = getAsRepairExcelColumnMapping(scope);
    const formConfig = normalizeAsRepairFormConfig(config);
    const fields = formConfig.fields;
    const fieldMap = getAsRepairExcelFieldMap(formConfig, scope);

    if (!fieldMap.modelName) {
        return {
            rows: [],
            skipped: 0,
            errors: ['A/S 설정에서 모델명 항목을 찾을 수 없습니다.'],
        };
    }

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
    if (!workbook.SheetNames.length) {
        return { rows: [], skipped: 0, errors: ['엑셀 시트가 비어 있습니다.'] };
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const ref = sheet['!ref'];
    if (!ref) {
        return { rows: [], skipped: 0, errors: ['엑셀에 데이터가 없습니다.'] };
    }

    const range = XLSX.utils.decode_range(ref);
    const rows = [];
    let skipped = 0;
    const isPersonalScope = scope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL;
    const startRow = isPersonalScope ? range.s.r + 1 : range.s.r;
    if (isPersonalScope && startRow > range.e.r) {
        return { rows: [], skipped: 0, errors: ['등록할 데이터 행이 없습니다.'] };
    }

    for (let r = startRow; r <= range.e.r; r += 1) {
        if (!rowHasAnyMappedData(sheet, r, columnMapping)) {
            skipped += 1;
            continue;
        }

        const values = buildEmptyAsRepairFormValues(formConfig);
        columnMapping.forEach(({ index, role }) => {
            const field = fieldMap[role];
            if (!field) {
                return;
            }
            const raw = getCellRaw(sheet, r, index);
            setExcelFieldValue(values, field, raw, role);
        });
        syncExcelRowMatchKeys(values, fieldMap, sheet, r, columnMapping);

        try {
            rows.push(applyAsRepairAutoValuesToForm(fields, values));
        } catch {
            rows.push(values);
        }
    }

    if (rows.length === 0) {
        errors.push('등록할 데이터 행이 없습니다.');
    }

    return { rows, skipped, errors };
};
